import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, ArrowLeft, Send, Calendar } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8080"}/api`;

// Fonction de formatage naturel avec espaces : 6 12 34 56 78
function formatFrenchPhone(val) {
  let digits = val.replace(/\D/g, "");
  if (digits.startsWith("0")) {
    digits = digits.substring(1);
  }
  digits = digits.slice(0, 9); // 9 chiffres après le +33

  if (digits.length === 0) return "";
  if (digits.length === 1) return digits;

  // 1er chiffre isolé, puis groupement par 2 chiffres
  const firstDigit = digits[0];
  const rest = digits.slice(1);
  const pairs = rest.match(/.{1,2}/g) || [];

  return `${firstDigit} ${pairs.join(" ")}`;
}

export default function PublicQuote() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    service_id: "",
    service_name: "",
    item_reference: "",
    description: "",
  });

  useEffect(() => {
    axios
      .get(`${API_BASE}/public/${slug}/quote-page`)
      .then((res) => setData(res.data))
      .catch((err) => {
        console.error(err);
        setError("Établissement introuvable ou inactif.");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const handleServiceChange = (serviceId) => {
    if (serviceId === "other") {
      setForm((prev) => ({ ...prev, service_id: "other", service_name: "Autre / Demande sur-mesure" }));
    } else {
      const s = data?.services?.find((srv) => srv.id === serviceId);
      setForm((prev) => ({
        ...prev,
        service_id: serviceId,
        service_name: s ? s.name : "",
      }));
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatFrenchPhone(e.target.value);
    setForm((prev) => ({ ...prev, phone: formatted }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim() || !form.description.trim()) {
      toast.error("Veuillez remplir vos coordonnées et détailler votre demande.");
      return;
    }

    const rawPhoneDigits = form.phone.replace(/\D/g, "");
    if (rawPhoneDigits.length < 9) {
      toast.error("Veuillez saisir un numéro de mobile valide (9 chiffres après le +33).");
      return;
    }

    const fullPhone = `+33${rawPhoneDigits}`;

    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: fullPhone,
        email: form.email.trim() || null,
        service_id: form.service_id || null,
        service_name: form.service_name || "Prestation générale",
        title: form.service_name ? `Demande : ${form.service_name}` : "Demande de devis personnalisée",
        item_reference: form.item_reference.trim(),
        description: form.description.trim(),
      };

      await axios.post(`${API_BASE}/public/${slug}/quotes`, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue lors de l'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-sm font-medium text-slate-500">Chargement de la page de devis...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-4 text-center">
        <h1 className="text-xl font-bold text-slate-900">{error || "Page introuvable"}</h1>
        <p className="mt-2 text-sm text-slate-500">Le lien semble incorrect ou a expiré.</p>
      </div>
    );
  }

  const { tenant, page_settings, services } = data;
  const brandColor = page_settings?.brand_color || "#2563EB";

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-12">
        <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Demande de devis envoyée !</h1>
          <p className="mt-2 text-sm text-slate-600">
            Merci <strong>{form.first_name}</strong>. L'équipe de <strong>{tenant.name}</strong> a bien reçu votre demande et vous répondra dans les plus brefs délais.
          </p>
          <div className="mt-6 rounded-xl border border-slate-100 bg-slate-50 p-4 text-left text-xs text-slate-600 space-y-1">
            <p><strong>Prestation :</strong> {form.service_name || "Demande personnalisée"}</p>
            {form.item_reference && <p><strong>Précision / Objet :</strong> {form.item_reference}</p>}
            <p><strong>Téléphone de contact :</strong> +33 {form.phone}</p>
          </div>
          <div className="mt-8 flex flex-col gap-2 sm:flex-row">
            <Link to={`/b/${slug}`} className="flex-1">
              <Button variant="outline" className="w-full">
                <Calendar className="mr-2 h-4 w-4 text-blue-600" /> Prendre rendez-vous directement
              </Button>
            </Link>
            <Button
              onClick={() => {
                setSubmitted(false);
                setForm({
                  first_name: "", last_name: "", phone: "", email: "",
                  service_id: "", service_name: "", item_reference: "", description: ""
                });
              }}
              style={{ backgroundColor: brandColor }}
              className="flex-1 text-white hover:opacity-95"
            >
              Nouvelle demande
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center justify-between">
          <Link to={`/b/${slug}`} className="flex items-center text-xs font-medium text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Accéder à la prise de rendez-vous directe
          </Link>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-slate-100 p-6 sm:p-8" style={{ borderTop: `5px solid ${brandColor}` }}>
            {page_settings?.logo_url && (
              <img src={page_settings.logo_url} alt={tenant.name} className="mb-4 h-12 w-auto object-contain" />
            )}
            <h1 className="font-display text-2xl font-bold text-slate-900">{tenant.name}</h1>
            <p className="mt-1 text-sm text-slate-500">
              {page_settings?.welcome_message || "Demandez une estimation personnalisée pour votre projet ou besoin spécifique."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
            {/* 1. Sélection de la prestation */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-800">Prestation concernée</Label>
              <Select onValueChange={handleServiceChange} value={form.service_id}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Sélectionnez une prestation ou choisissez 'Autre'" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} {s.price ? `(dès ${s.price} €)` : ""}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Autre demande / Projet sur-mesure</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 2. Précision complémentaire universelle */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-800">
                Objet ou référence du projet <span className="text-xs font-normal text-slate-400">(facultatif)</span>
              </Label>
              <Input
                value={form.item_reference}
                onChange={(e) => setForm({ ...form, item_reference: e.target.value })}
                placeholder="Ex : Référence équipement, surface, modèle, lieu de prestation..."
                className="h-11"
              />
            </div>

            {/* 3. Description détaillée */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-sm font-semibold text-slate-800">Détails de votre demande *</Label>
                <span className="text-xs text-slate-400">Précisez vos besoins</span>
              </div>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={5}
                required
                placeholder="Décrivez votre besoin :
- Nature du projet ou problème rencontré
- Contraintes techniques ou éléments déjà en votre possession
- Délais souhaités..."
                className="text-sm leading-relaxed"
              />
            </div>

            {/* 4. Coordonnées client */}
            <div className="border-t border-slate-100 pt-5 space-y-4">
              <Label className="text-sm font-semibold text-slate-800">Vos coordonnées</Label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs text-slate-600">Prénom *</Label>
                  <Input
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    required
                    placeholder="Jean"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Nom *</Label>
                  <Input
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    required
                    placeholder="Dupont"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Numéro de téléphone mobile *</Label>
                  <div className="relative mt-1 flex h-11 items-stretch rounded-md shadow-sm">
                    <span className="inline-flex items-center justify-center rounded-l-md border border-r-0 border-input bg-slate-100 px-3 text-sm font-semibold text-slate-700 select-none">
                      🇫🇷 +33
                    </span>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={handlePhoneChange}
                      required
                      placeholder="6 12 34 56 78"
                      className="h-11 rounded-l-none text-sm font-medium focus-visible:z-10"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Email (optionnel)</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jean.dupont@exemple.fr"
                    className="mt-1 h-11"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              style={{ backgroundColor: brandColor }}
              className="w-full h-12 text-base font-semibold text-white hover:opacity-95 shadow-sm transition-opacity"
            >
              {submitting ? "Transmission en cours..." : (
                <>
                  <Send className="mr-2 h-4 w-4" /> Envoyer ma demande de devis
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}